<template>
    <div class="vue-reservation-step-1">
        <div class="background-wrapper">
            <div class="row">
                <div class="col-12">
                    <h2 class="virtual-h3">{{ translations.locationDropdownTitle }}</h2>

                    <div class="location-picker" v-if="getShowLocationDropdown">
                        <aut-location-dropdown
                            :hide-label="true"
                            @active-location="handleActiveLocation"
                            :vehicle-id="vehicleId"
                        ></aut-location-dropdown>

                        <p class="small">{{ translations.locationDropdownInfo }}</p>
                    </div>

                    <aut-active-location-card
                        v-if="hasActiveLocation"
                        :location="getActiveLocation"
                        :active="true"
                    ></aut-active-location-card>

                    <hr>

                    <template v-if="hasActiveVehicle && getActiveVehicleWrapper.amount_of_vehicles > 1">
                        <VehicleSelectWidget
                            @vehicle-selected="handleVehicleSelected"
                        />

                        <hr>
                    </template>

                    <h2 class="virtual-h3" ref="rentalPeriodTitle">{{ translations.rentalPeriodTitle }}</h2>

                    <div class="alert alert-gray-light" v-if="hasBookableFrom && hasAlternativeVehicle">
                        <p class="m-0">
                            Deze auto is pas vanaf <span class="font-weight-bold">{{ getActiveVehicle.bookable_from|formatDate }}</span> te reserveren.
                            We hebben <span class="font-weight-bold">{{ getAlternativeVehicle.title }}</span> als alternatief beschikbaar, deze is wel direct reserveerbaar!

                            <a
                                class="simple-button"
                                :href="$router.resolve({ name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: { vehicleId: getAlternativeVehicle.id, locationId: getActiveLocation.id } }).href"
                            >Huur {{ getAlternativeVehicle.title }}</a>
                        </p>
                    </div>

                    <div class="date-picker row">
                      <div class="form-item col-12">
                        <div class="date-picker-wrapper" @click.stop="handleToggleDatePicker">
                          <label id="date-picker-label">{{ translations.datetimePickerLabel }}</label>
                          <span class="date-picker-show">{{ customDateFormat(this.dateRange.startDate) + ' - ' + customDateFormat(this.dateRange.endDate)}}</span>
                        </div>
                      </div>
                    </div>

                    <div class="date-time-picker row">
                        <div class="form-item col-lg-6">
                            <div class="time-input-wrapper">
                                <label for="departure-time">{{ translations.pickupLabel }}</label>
                                <select id="departure-time" name="departure-time"
                                        :value="getDepartureTime"
                                        @change="setDepartureTime($event.target.value); updateVehicleRates(vehicleId);">
                                    <option
                                        v-for="time in getTimeRange"
                                        :value="time">{{ time }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="form-item col-lg-6">
                            <div class="time-input-wrapper">
                                <label>{{ translations.returnLabel }}</label>
                                <select id="return-time" name="return-time"
                                        :value="getReturnTime"
                                        @change="setReturnTime($event.target.value); updateVehicleRates(vehicleId);">
                                    <option
                                        v-for="time in getTimeRange"
                                        :value="time">{{ time }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="form-item col-12 col-xxl-6">
                            <date-range-picker
                                ref="dateRangePicker"
                                opens="right"
                                :single-date-picker="dateRangePickerType"
                                v-model="dateRange"
                                :date-format="dateRangeFormat"
                                :locale-data="getLocaleData()"
                                :linked-calendars="true"
                                :auto-apply="true"
                                :ranges="false"
                                @toggle="handleDateRangeToggle"
                                @start-selection="handleDateRangeStartSelection"
                                @update="handleDateRangePickerUpdate"
                                :min-date="minDate"
                                :max-date="maxDate"
                            ></date-range-picker>
                        </div>
                    </div>

                    <div class="alert alert-warning" v-if="isTemporarilyClosedWarning">
                        <p class="m-0">Tijdelijk gesloten.</p>
                    </div>

                    <div class="alert alert-warning" v-if="hasDateError">
                        <p class="m-0">{{ translations.datesErrorText }}</p>
                    </div>

                    <div class="alert alert-warning" v-if="hasExtraDay">
                        <p class="m-0" v-html="translations.extraDayWarning"></p>
                    </div>

                    <div class="alert alert-warning" v-if="isAfterPickupTime && hasActiveLocation">
                        <p class="m-0">
                          Na {{ getActiveLocation.endTimeReservation }}:00 uur is het niet meer mogelijk om online een voertuig voor de volgende dag te reserveren.
                        </p>
                    </div>

                    <div class="alert alert-warning" v-if="isNotMinimalWeek">
                        <p class="m-0">
                            De minimale huurperiode voor dit voertuig is 1 week. U kunt dit voertuig niet reserveren voor minder dan 7 dagen.
                        </p>
                    </div>

                    <div class="book-earlier" v-if="hasActiveLocation">
                        <strong>{{ translations.bookEarlier }}</strong>
                        Bel <a :href="`tel:${getActiveLocation.phoneNumber}`">{{ getActiveLocation.phoneNumber }}</a>
                    </div>

                    <div class="usp-wrapper mt-3 mt-md-5" v-if="uspText" v-html="uspText"></div>
                </div>
            </div>
        </div>

      <div class="button-wrapper-mobile row">
        <div class="col-12 d-flex justify-content-between">
          <router-link class="btn-primary me-0"
                       :class="{ disabled: isDisabled }"
                       :to="{ name: this.isBusiness ? 'stepTwoBusiness' : 'stepTwo', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id } }">
            {{ translations.nextOptionsText }}
          </router-link>
        </div>
      </div>


      <div class="button-wrapper row">
            <div class="col-12 d-flex justify-content-between">
                <button @click="handleGoBack" class="btn-ghost btn-no-arrow">{{ translations.backText }}</button>

                <div class="d-flex mb-0 total-price-button mr-1">
                    <div class="mr-auto p-2 price-label align-middle">{{ translations.rentalPrice }}</div>
                    <div class="p-2 price-total">{{ getTotalRentalPrice | vatExcluder | currencyFormatter }}</div>
                </div>

                <router-link v-if="hasActiveVehicle" class="btn-primary me-0"
                             :class="{ disabled: isDisabled }"
                             :to="{ name: this.isBusiness ? 'stepTwoBusiness' : 'stepTwo', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: vehicleWrapperId } }">
                    {{ translations.nextText }}
                </router-link>
            </div>
            <div class="col-12 text-end opacity-50 additional-text">
                <small>{{ translations.selectAnyAdditionalOptions }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import moment from "moment";
import 'moment/locale/nl';
import Dropdown from "../Location/Dropdown";
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import DateRangePicker from 'vue2-daterange-picker/src/index.js'
import {nl} from 'vuejs-datepicker/dist/locale';
import translations from "../../mixins/translations";
import PhoneNumberFormatter from "../../mixins/PhoneNumberFormatter";
import PopUpModal from "./PopUpModal.vue";
import ActiveCard from "../Location/ActiveCard.vue";
import CurrencyFormatter from "../../mixins/CurrencyFormatter";
import VehicleSelectWidget from "./Vehicle/VehicleSelectWidget.vue";

export default {
    name: "ReservationStepOne",

    mixins: [translations, PhoneNumberFormatter, CurrencyFormatter],

    components: {
        VehicleSelectWidget,
        'aut-location-dropdown': Dropdown,
        'aut-active-location-card': ActiveCard,
        DateRangePicker,
    },

    props: {
        uspText: {
            type: String,
            default: '',
        }
    },

    data() {
        return {
            nl: nl,
            isBusiness: null,
            vehicleId: null,
            vehicleWrapperId: null,
            locationId: null,
            pickupPoint: null,
            dateRange: {
                startDate: null,
                endDate: null,
            },
            sundayDisabled: true,
            selectedStartDate: null,
            isSelectingEndDate: false,
            minDate: null,
            maxDate: null,
        }
    },

    filters: {
        formatDate(value) {
            if (!value) return value;
            return moment(value).format('DD MMMM YYYY');
        }
    },

    computed: {
        ...mapGetters([
            'getDepartureDate',
            'getDepartureTime',
            'getReturnDate',
            'getReturnTime',
            'getActiveLocation',
            'getActiveDiscountCode',
            'getTimeRange',
            'getCurrentStep',
            'getActiveRentalOptions',
            'getIsSundaysOpen',
            'getActiveVehicle',
            'getDisabledReservationDates',
            'getShowLocationDropdown',
            'isAfterPickupTime',
            'getPopUpSettings',
            'getDays',
            'hasActiveLocation',
            'getBeginCheckoutStarted',
            'getTotalRentalPrice',
            'getActiveVehicleWrapper',
            'hasAlternativeVehicle',
            'hasBookableFrom',
            'hasBookableTo',
            'getAlternativeVehicle'
        ]),

        isTemporarilyClosedWarning() {
            if (!this.hasActiveLocation || !this.getActiveLocation.isTemporarilyClosed) {
                return false;
            }

            const start = moment(this.getActiveLocation.temporaryClosureStart);
            const pickup = moment(this.getDepartureDate);

            // Show warning if within 7 days of closure start (purely informative if no overlap)
            const now = moment();
            if (now.isBefore(start) && start.diff(now, 'days') <= 7) {
                return true;
            }

            return this.isTemporarilyClosedOverlap;
        },

        isTemporarilyClosedOverlap() {
            if (!this.hasActiveLocation || !this.getActiveLocation.isTemporarilyClosed) {
                return false;
            }

            const start = moment(this.getActiveLocation.temporaryClosureStart);
            const end = moment(this.getActiveLocation.temporaryClosureEnd);
            const pickup = moment(this.getDepartureDate);
            const returnDate = moment(this.getReturnDate);

            // Check if actual booking period overlaps with closure period
            if (pickup.isBetween(start, end, 'day', '[]') || returnDate.isBetween(start, end, 'day', '[]')) {
                return true;
            }

            if (start.isBetween(pickup, returnDate, 'day', '[]')) {
                return true;
            }

            return false;
        },

        hasLocationIdProp() {
            return !lodash.isNil(this.locationId);
        },

        hasComputedLocationId() {
            return !lodash.isNil(this.computedLocationId);
        },

        computedLocationId() {
            if (!this.hasLocationIdProp) {
                return this.getActiveLocation.id;
            }

            return this.locationId
        },

        hasActiveLocation() {
            return !lodash.isEmpty(this.getActiveLocation);
        },

        hasActiveVehicle() {
            return !lodash.isEmpty(this.getActiveVehicle)
        },

        hasDisabledReservationDates() {
            let filledDates = lodash.filter(this.getDisabledReservationDates, (item) => {
                return item !== '';
            });

            return !lodash.isEmpty(filledDates);
        },

        disabledReservationDateRange() {
            if (!this.hasDisabledReservationDates) {
                return false;
            }

            return lodash.map(this.getDisabledReservationDates, (item) => {
                return new Date(item);
            })
        },

        disabledDates() {
            let disabledToDate = moment().toDate();
            let disabledFromDate = null;

            if(this.hasBookableFrom && moment(this.getActiveVehicle.bookable_from) > moment()) {
                disabledToDate = moment(this.getActiveVehicle.bookable_from).subtract(1, 'day').toDate();
            }

            if(this.hasBookableTo && moment(this.getActiveVehicle.bookable_to) > moment()) {
                disabledFromDate = moment(this.getActiveVehicle.bookable_to).add(1, 'day').toDate();
            }

            let disabledDays = [0];

            if (this.getIsSundaysOpen) {
                disabledDays = [];
            }

            let disabledDatesOptions = {
                to: disabledToDate,
                from: disabledFromDate,
                days: disabledDays,
                dates: [],
            }

            if (this.hasDisabledReservationDates && !this.getActiveLocation.holidaysOpen) {
                lodash.merge(disabledDatesOptions, {
                    dates: this.disabledReservationDateRange,
                });
            }

            console.log(disabledDatesOptions);

            return disabledDatesOptions;
        },

        disabledReturnDates() {
            let disabledToDate = moment().add(1, 'days').toDate();

            let disabledDays = [];

            if (this.getActiveLocation.sundaysCompletelyClosed) {
                disabledDays = [0];
            }

            return {
                to: disabledToDate,
                days: disabledDays
            }
        },

        hasDateError() {
            let departureDate = moment(this.getDepartureDate).format('YYYY-MM-DD');
            let returnDate = moment(this.getReturnDate).format('YYYY-MM-DD');

            if (parseFloat(this.getDepartureTime) > parseFloat(this.getReturnTime) && (departureDate > returnDate || lodash.isEqual(departureDate, returnDate))) {
                return true;
            }

            return departureDate > returnDate || (lodash.isEqual(departureDate, returnDate) && lodash.isEqual(this.getReturnTime, this.getDepartureTime));
        },

        hasExtraDay() {
            let departureDate = moment(this.getDepartureDate).format('YYYY-MM-DD');
            let returnDate = moment(this.getReturnDate).format('YYYY-MM-DD');

            return this.getReturnTime !== '08:00' && !lodash.isEqual(departureDate, returnDate);
        },

        isNotMinimalWeek() {
            return this.getActiveVehicle.reserve_week && this.getDays < 7;
        },

        isDisabled() {
            return lodash.isEmpty(this.getActiveLocation) || this.hasDateError || this.isAfterPickupTime || this.isNotMinimalWeek || this.isTemporarilyClosedOverlap;
        },

        canDepartOnSunday() {
            return moment().add(1, 'days').day() === 0 && !this.getIsSundaysOpen;
        },

        dateRangePickerType() {
            const singleCalendar = 'range'; // see https://innologica.github.io/vue2-daterange-picker/#props
            const doubleCalendar = false; // see https://innologica.github.io/vue2-daterange-picker/#props

            return window.matchMedia('(max-width: 767px)').matches ? singleCalendar : doubleCalendar;
        },
    },

    async created() {
        this.isBusiness = this.$route.meta.isBusiness ?? false;

        this.vehicleId = this.$route.query.vehicleId
        this.vehicleWrapperId = this.$route.query.vehicleWrapperId
        this.locationId = this.$route.query.locationId ?? null

        if (lodash.isNil(this.locationId)) {
            await this.fetchUserFavoriteLocation();
        }

        this.setCurrentStep(this.$route.name);
        await this.setActiveVehicle(this.vehicleId);
        this.setActiveVehicleWrapper({vehicleWrapperId: this.vehicleWrapperId, locationId: this.locationId});

        if (!lodash.isNil(this.locationId)) {
            this.setShowLocationDropdown(false);
            this.fetchLocation();
        }

        this.setStepTwoDisabled(this.isDisabled);

        if ((lodash.isNil(this.getDepartureDate) || lodash.isNil(this.getReturnDate)) && lodash.isNil(this.locationId)) {
            this.setDefaultDates();
        }

        this.setDefaultDatesAtCreated();
    },

    methods: {
        ...mapActions([
            'setActiveLocation',
            'setActivePickupPoint',
            'setActiveVehicle',
            'setDepartureDate',
            'setReturnDate',
            'setDepartureTime',
            'setReturnTime',
            'updateVehicleRates',
            'updateLocationOpeningHours',
            'setCurrentStep',
            'resetActiveRentalOptions',
            'checkDiscountCodeValid',
            'setRentalOptions',
            'setStepTwoDisabled',
            'setShowLocationDropdown',
            'setDays',
            'setActiveVehicleWrapper',
        ]),

        handleActiveLocation(location) {
            this.setActiveLocation(location);
            this.setActivePickupPoint(location.pickupPoints[0]);
            this.setDefaultDates();
            this.setDefaultDatesAtCreated();
            this.setActiveVehicleWrapper({vehicleWrapperId: this.vehicleWrapperId, locationId: this.getActiveLocation.id});
            this.$router.replace({
                name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: {
                    vehicleId: this.getActiveVehicle.id,
                    vehicleWrapperId: this.getActiveVehicleWrapper.vehicle_wrapper_id,
                    locationId: this.getActiveLocation.id,
                }
            });
        },

        async fetchUserFavoriteLocation() {
            if (typeof drupalSettings !== 'undefined' && (drupalSettings.user?.uid ?? 0) > 0) {
                try {
                    const response = await axios.get('/api/customer/my-location');
                    if (response.data && response.data.id) {
                        const favoriteLocationId = response.data.id;

                        // Check if this location is available for the current vehicle
                        let url = `/api/locations?limit=500`;
                        if (!lodash.isNil(this.vehicleId)) {
                            url = url.concat(`&vehicleId=${this.vehicleId}`);
                        }

                        const locationsResponse = await axios.get(url);
                        const availableLocations = locationsResponse.data;

                        const isAvailable = lodash.find(availableLocations, (loc) => {
                            return parseInt(loc.id) === parseInt(favoriteLocationId);
                        });

                        if (isAvailable) {
                            this.locationId = favoriteLocationId;
                            this.setShowLocationDropdown(false);
                        } else {
                            this.setShowLocationDropdown(true);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user favorite location:', error);
                }
            }
        },

        fetchLocation() {
            axios.get(`/api/location-details?locationId=${this.locationId}`)
                .then(response => {
                    if (response.data.redirect_to) {
                      window.location = response.data.redirect_to;
                    }

                    this.setActiveLocation(response.data);
                    this.setActivePickupPoint(response.data.pickupPoints[0]);
                    this.setDefaultDates();
                });
        },

        setDefaultDatesAtCreated() {
            if(this.hasBookableFrom && moment(this.getActiveVehicle.bookable_from) > moment()) {
                this.minDate = moment(this.getActiveVehicle.bookable_from).subtract(1, 'day').toDate();
            } else {
                this.minDate = moment().toDate();
            }
            if(this.hasBookableTo && moment(this.getActiveVehicle.bookable_to) > moment()) {
                this.maxDate = moment(this.getActiveVehicle.bookable_to).toDate();
            }
            console.log(this.maxDate);

            this.dateRange.startDate = moment(this.getNextAvailableDepartureDateForRange());
            this.dateRange.endDate = moment(this.getNextAvailableDepartureDateForRange()).add(1, 'day');

            if (this.getDepartureDate) {
                this.dateRange.startDate = this.getDepartureDate;
            }

            if (this.getReturnDate) {
                this.dateRange.endDate = this.getReturnDate;
            }

            this.selectedStartDate = this.getDepartureDate ? moment(this.getDepartureDate).toDate() : this.getNextAvailableDepartureDateForRange();
        },

        setDefaultDates() {
            let nextAvailableDate = this.getNextAvailableDepartureDateForRange();

            if (lodash.isNil(nextAvailableDate)) {
                return;
            }

            let amountOfDays = 1;

            if (this.getActiveLocation.sundaysCompletelyClosed && lodash.isEqual(moment(nextAvailableDate).add(amountOfDays, 'days').weekday(), 6)) {
                amountOfDays = 2;
            }

            if(this.getDepartureDate !== null && moment(this.getDepartureDate).isSameOrAfter(moment(nextAvailableDate))) {
                nextAvailableDate = this.getDepartureDate;
            }
            this.setDepartureDate(moment(nextAvailableDate).format());
            this.setDefaultReturnDate(nextAvailableDate, amountOfDays);
            this.updateVehicleRates(this.vehicleId);
            this.updateLocationOpeningHours();
            this.setDays(moment(this.getReturnDate).diff(this.getDepartureDate, 'days'));
        },

        getNextAvailableDate(date, skipDisabledDates = false) {
            let nextAvailableDate = this.isClosed(date, skipDisabledDates) ? null : date;

            for (let i = 0; nextAvailableDate === null; i++) {
                let nextDay = moment(date).add(i, 'days').toDate();
                nextAvailableDate = this.isClosed(nextDay, skipDisabledDates) ? null : nextDay;
            }

            return nextAvailableDate;
        },

        getNextAvailableDepartureDateForRange() {
            if (this.hasBookableFrom  && moment(this.getActiveVehicle.bookable_from) > moment()) {
                return this.getNextAvailableDate(moment(this.getActiveVehicle.bookable_from).toDate())
            }

            const date = this.getDepartureDate ? moment(this.getDepartureDate).toDate() : moment().toDate();
            return this.getNextAvailableDate(date);
        },

        getNextAvailableReturnDateForRange() {
            const date = this.getReturnDate ? moment(this.getReturnDate).toDate() : moment().toDate();
            return this.getNextAvailableDate(date, true);
        },

        setDefaultReturnDate(date, amountOfDays = 1) {
            if (this.getReturnDate) {
                this.setReturnDate(moment(this.getNextAvailableReturnDateForRange()).format());
                return;
            }

            this.setReturnDate(moment(date).add(amountOfDays, 'days').format());
        },

        handleGoBack() {
            if (!this.hasActiveLocation) {
                window.location = '/autos-en-prijzen';
                return
            }

            window.location = this.getActiveLocation.url;
        },

        customDateFormat(date) {
            moment.locale('nl');
            return moment(date).format('dd D MMMM');
        },

        dateRangeFormat(classes, date) {
            classes.disabled = this.isClosed(date);
            return classes;
        },

        isClosed(date, skipDisabledDates = false) {
            let dt = new Date(date);
            dt.setHours(0, 0, 0, 0);

            if (this.maxDate && dt > new Date(this.maxDate)) {
                return true;
            }

            if (this.hasActiveLocation && this.getActiveLocation.isTemporarilyClosed) {
                const start = moment(this.getActiveLocation.temporaryClosureStart).startOf('day');
                const end = moment(this.getActiveLocation.temporaryClosureEnd).endOf('day');
                const current = moment(date);
                if (current.isBetween(start, end, 'day', '[]')) {
                    return true;
                }
            }

            return (
                date.toLocaleDateString() === this.disabledDates.to.toLocaleDateString() // today is always disabled.
                ||
                date <= this.disabledDates.to // everything before today is always disabled.
                ||
                this.disabledDates.dates.find(x => x.toLocaleDateString() === date.toLocaleDateString()) // closed on all specified disabled dates.
                ||
                (date.getDay() === 0 && this.getActiveLocation.sundaysCompletelyClosed) // closed if date is sunday and the location is completely closed on sundays.
                ||
                (date.getDay() === 0 && !this.isSelectingEndDate && this.disabledDates.days.includes(0) && !skipDisabledDates) // closed if date is sunday and the user is not selecting an end date and sundays are disabled on the location.
                ||
                (date.getDay() === 0 && this.isSelectingEndDate && date <= this.selectedStartDate && this.disabledDates.days.includes(0) && !skipDisabledDates) // closed if date is sunday before the selected start date while the user is selecting an end date and while sundays are disabled on the location, because returning on a sunday is allowed.
                ||
                (date.getDay() !== 0 && this.disabledDates.days.includes(date.getDay()) && !skipDisabledDates) // all other disabled days, except sunday
            );
        },

        handleDateRangeToggle() {
            this.sundayDisabled = true;
            this.isSelectingEndDate = false;
        },

        handleDateRangeStartSelection(value) {
            this.sundayDisabled = false;
            this.isSelectingEndDate = true;
            this.selectedStartDate = value;
        },

        handleDateRangePickerUpdate(value) {
            let departureDate = moment(value.startDate).format('YYYY-MM-DD');
            let returnDate = moment(value.endDate).format('YYYY-MM-DD');

            this.setReturnTime(this.getTimeRange.at(0));

            if (departureDate === returnDate) {
                this.setReturnTime(this.getTimeRange.at(-1));
            }

            let amountOfDays = 1;

            if (this.getActiveLocation.sundaysCompletelyClosed && lodash.isEqual(moment(value.startDate).add(amountOfDays, 'days').weekday(), 6)) {
                amountOfDays = 2;
            }

            if (lodash.isEqual(departureDate, returnDate) || departureDate > returnDate) {
                this.setReturnDate(moment(value.startDate).add(amountOfDays, 'days').format());
            }

            this.setDepartureDate(moment(value.startDate).format());
            this.setReturnDate(moment(value.endDate).format());

            if (this.getActiveDiscountCode !== "") {
              this.checkDiscountCodeValid(this.getActiveDiscountCode);
            }

            this.updateVehicleRates(this.vehicleId);
            this.updateLocationOpeningHours();
        },

        getLocaleData() {
            return {
                direction: 'ltr',
                format: 'dd-mm-yyyy',
                separator: ' - ',
                applyLabel: 'Apply',
                cancelLabel: 'Cancel',
                weekLabel: 'W',
                customRangeLabel: 'Custom Range',
                daysOfWeek: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
                monthNames: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
                firstDay: 1,
            };
        },

        handleToggleDatePicker() {
            this.$refs.dateRangePicker.togglePicker();

            if (!window.matchMedia('(max-width: 767px)').matches) {
                return;
            }

            this.$refs.rentalPeriodTitle.scrollIntoView();
        },

        handleVehicleSelected(payload) {
            if (!payload) {
                return;
            }

            this.vehicleId = payload
        },
    },

    watch: {
        isDisabled(val) {
            this.setStepTwoDisabled(this.isDisabled);
        },

        getDays(val) {
            if (lodash.isNil(this.getPopUpSettings.days) || lodash.isNil(this.getDays) || this.getDays < this.getPopUpSettings.days) {
                return;
            }

            this.$modal.show(PopUpModal, {
                label: `Pop Up Modal`,
                transition: 'fade',
                classes: ['small-modal']
            })
        }
    }
}
</script>
